<template>
  <div class="recent-activity">
    <h3>Recent activity</h3>

    <!-- Physical Performance — last results (Pro-tier / GPS clubs only) -->
    <div class="ra-card" v-if="showPhysical">
      <h4>Physical Performance</h4>
      <template v-if="latestPhysical">
        <p class="ra-date" v-if="latestPhysical.date">Last tested {{ formatDate(latestPhysical.date) }}</p>
        <ul class="ra-list">
          <li v-for="m in physicalMetrics" :key="m.key">
            <span>{{ m.label }}</span><strong>{{ m.value }}</strong>
          </li>
        </ul>
      </template>
      <p v-else class="ra-empty">No physical performance results yet.</p>
    </div>

    <!-- Growth & Maturation (Bio-banding) -->
    <div class="ra-card">
      <h4>Growth &amp; Maturation</h4>
      <template v-if="loadingBio">
        <p class="ra-empty">Loading…</p>
      </template>
      <template v-else-if="bioAge">
        <p class="ra-subhead">Biological Age Comparison</p>
        <ul class="ra-list">
          <li><span>Chronological Age</span><strong>{{ num(bioAge.decimalAge, 1) }}</strong></li>
          <li><span>Biological Age</span><strong>{{ num(bioAge.bioAge, 1) }}</strong></li>
          <li><span>Difference</span><strong>{{ signed(bioAge.bioAgeDifference, 1) }}</strong></li>
          <li><span>Estimated Adult Height</span><strong>{{ num(bioAge.childEstimatedHeight, 1) }} cm</strong></li>
          <li><span>Height Achieved</span><strong>{{ num(bioAge.childEstimatedHeightAchieved, 1) }}%</strong></li>
        </ul>
      </template>
      <p v-else class="ra-empty">Not yet calculated in the Bio-banding calculator.</p>
    </div>

    <!-- Games played (recent, with mins) -->
    <div class="ra-card">
      <h4>Games played</h4>
      <ul class="ra-games" v-if="recentGames.length">
        <li v-for="(g, i) in recentGames" :key="i">
          <span class="ra-game-date">{{ formatDate(g.session_date) }}</span>
          <span class="ra-game-type">
            {{ g.session_type || 'Game' }}
            <span v-if="gameLevel(g)" class="ra-level" :class="gameLevel(g)">
              {{ teamULabel(g.team_age_group) }}
            </span>
          </span>
          <strong class="ra-game-mins">{{ g.session_mins }} mins</strong>
        </li>
      </ul>
      <p v-else class="ra-empty">No games logged yet.</p>
    </div>
  </div>
</template>

<script>
import { PlayerPhysicalPerformance } from "@/services/player-physical-performance-service.js";
import { isFeatureEnabled } from "@/config/tenant";

export default {
  name: "RecentActivity",

  props: {
    selectedUserData: { type: Object, default: () => ({}) },
    // allPlayerPhysicalPerformanceData (ascending by date)
    physicalPerformance: { type: Array, default: () => [] },
    // playerDailyMins (per-session GPS rows)
    games: { type: Array, default: () => [] },
  },

  data() {
    return {
      bioAge: null,
      loadingBio: false,
    };
  },

  computed: {
    // GPS/physical testing is a Pro-tier feature — hide the card entirely for
    // grassroots clubs (config/tiers.json gpsReports: false) so players don't
    // see an empty "No physical performance results yet" block.
    showPhysical() {
      return isFeatureEnabled("gpsReports");
    },
    playerId() {
      return this.selectedUserData && this.selectedUserData.id;
    },

    latestPhysical() {
      const arr = this.physicalPerformance;
      return Array.isArray(arr) && arr.length ? arr[arr.length - 1] : null;
    },

    // Only show metrics that are present on the latest record.
    physicalMetrics() {
      const p = this.latestPhysical;
      if (!p) return [];
      const defs = [
        { key: "tenM_sprint_time", label: "10m sprint", suffix: "s" },
        { key: "fortyM_sprint_time", label: "30/40m sprint", suffix: "s" },
        { key: "cuntermovement_jump", label: "Countermovement jump", suffix: "cm" },
        { key: "reactive_strength_index", label: "Reactive strength index", suffix: "" },
        { key: "thirty_15_intermittent_fitness_test", label: "30-15 fitness", suffix: "" },
        { key: "height", label: "Height", suffix: "cm" },
        { key: "weight", label: "Weight", suffix: "kg" },
      ];
      return defs
        .filter((d) => p[d.key] !== null && p[d.key] !== undefined && p[d.key] !== "" && Number(p[d.key]) !== 0)
        .map((d) => ({ key: d.key, label: d.label, value: `${p[d.key]}${d.suffix ? " " + d.suffix : ""}` }));
    },

    recentGames() {
      const arr = Array.isArray(this.games) ? this.games : [];
      const isGame = (s) => {
        if (s.gameId && Number(s.gameId) > 0) return true;
        const t = (s.session_type || "").toLowerCase();
        return /game|friendly|cup|festival/.test(t) && !/training|warm|technical|station|^session$/.test(t);
      };
      return arr
        .filter((s) => isGame(s) && Number(s.session_mins) > 0)
        .slice()
        .sort((a, b) => new Date(b.session_date) - new Date(a.session_date))
        .slice(0, 5);
    },
  },

  watch: {
    playerId() {
      this.loadBio();
    },
  },

  mounted() {
    this.loadBio();
  },

  methods: {
    async loadBio() {
      this.bioAge = null;
      if (!this.playerId) return;
      this.loadingBio = true;
      try {
        const res = await PlayerPhysicalPerformance.getBioAgeResults(this.playerId);
        const r = res && res.data;
        this.bioAge = r && typeof r === "object" && r.bioAge !== null && r.bioAge !== undefined ? r : null;
      } catch (e) {
        this.bioAge = null;
      } finally {
        this.loadingBio = false;
      }
    },

    num(v, dp = 1) {
      const n = Number(v);
      return isNaN(n) ? "—" : n.toFixed(dp);
    },

    signed(v, dp = 1) {
      const n = Number(v);
      if (isNaN(n)) return "—";
      return (n >= 0 ? "+" : "") + n.toFixed(dp);
    },

    // Age-group label for a team's birth year, e.g. 2007 -> "U19".
    teamULabel(ageGroup) {
      const year = parseInt(ageGroup, 10);
      if (isNaN(year)) return "";
      return "U" + (new Date().getFullYear() - year);
    },

    // Flag whether a game was played UP/DOWN: only for non-primary teams,
    // comparing the game's team age_group (birth year) to the player's.
    gameLevel(g) {
      const primaryTeamId = this.selectedUserData && this.selectedUserData.teamId;
      if (!g.teamId || Number(g.teamId) === Number(primaryTeamId)) return null;
      const playerYear = parseInt(this.selectedUserData && this.selectedUserData.age_group, 10);
      const teamYear = parseInt(g.team_age_group, 10);
      if (isNaN(playerYear) || isNaN(teamYear)) return null;
      if (teamYear < playerYear) return "up";
      if (teamYear > playerYear) return "down";
      return null;
    },

    formatDate(value) {
      if (!value) return "";
      const d = new Date(String(value).replace(" ", "T"));
      return isNaN(d.getTime()) ? String(value) : d.toLocaleDateString();
    },
  },
};
</script>

<style scoped>
.recent-activity {
  flex: 2 1 340px;
  min-width: 300px;
}
.recent-activity > h3 {
  margin-bottom: 0.75rem;
}
.ra-card {
  background: #f9fafb;
  border: 1px solid #eee;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 14px;
}
.ra-card h4 {
  margin: 0 0 8px;
  font-size: 1rem;
}
.ra-subhead {
  margin: 0 0 6px;
  font-size: 0.8rem;
  font-weight: 700;
  color: #6b7280;
}
.ra-date {
  margin: 0 0 8px;
  font-size: 0.8rem;
  color: #6b7280;
}
.ra-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.ra-list li {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 4px 0;
  border-bottom: 1px solid #f0f0f0;
  font-size: 0.88rem;
}
.ra-list li:last-child {
  border-bottom: none;
}
.ra-list span {
  color: #4b5563;
}
.ra-games {
  list-style: none;
  margin: 0;
  padding: 0;
}
.ra-games li {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
  border-bottom: 1px solid #f0f0f0;
  font-size: 0.85rem;
}
.ra-games li:last-child {
  border-bottom: none;
}
.ra-game-date {
  flex: 0 0 auto;
  color: #6b7280;
}
.ra-game-type {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ra-game-mins {
  flex: 0 0 auto;
}
.ra-level {
  display: inline-block;
  margin-left: 6px;
  padding: 0 6px;
  border-radius: 4px;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
  background: #eef1f4;
  color: #4b5563;
}
.ra-empty {
  margin: 0;
  font-size: 0.85rem;
  color: #9ca3af;
}
</style>
